import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Coffee, Clock, CheckCircle, AlertTriangle,
    RefreshCcw, Timer, Bell, Check,
    AlertCircle, X, Play, Maximize2, Minimize2,
    Volume2, VolumeX, Home, Flame, Thermometer
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import MobilePageHeader from '../components/MobilePageHeader';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = () => {
    const token = localStorage.getItem('token');
    return {
        get: async (url) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        put: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        },
        post: async (url, data) => {
            const res = await fetch(`${API_BASE}/api${url}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { data: await res.json() };
        }
    };
};

// ========================================
// TICKET DE COMMANDE BOISSONS CHAUDES
// ========================================

const HotBeverageTicket = ({ 
    order, 
    onCommencer, 
    onPret, 
    onServi,
    onProblem,
    isFullscreen = false 
}) => {
    const statusColors = {
        nouveau: 'border-amber-400 bg-amber-50',
        en_preparation: 'border-orange-500 bg-orange-50',
        pret: 'border-emerald-500 bg-emerald-50',
        probleme: 'border-red-500 bg-red-50'
    };

    const priorityColors = {
        urgent: 'bg-red-600',
        normal: 'bg-amber-500',
        nouveau: 'bg-blue-500'
    };

    const status = order.boissons_chaudes_status || 'nouveau';
    const priority = order.priority || 'nouveau';
    
    return (
        <div 
            className={cn(
                "rounded-xl border-2 shadow-md overflow-hidden transition-all",
                statusColors[status] || statusColors.nouveau,
                isFullscreen ? "text-base" : "text-sm"
            )}
            data-testid={`hot-beverage-ticket-${order.order_id}`}
        >
            {/* Header */}
            <div className={cn(
                "flex items-center justify-between px-3 py-2 text-white",
                priorityColors[priority]
            )}>
                <div className="flex items-center gap-2">
                    <Coffee className={cn("flex-shrink-0", isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                    <span className={cn("font-bold", isFullscreen ? "text-lg" : "text-sm")}>
                        {order.is_takeaway ? 'À EMPORTER' : order.table_number ? `Table ${order.table_number}` : `#${order.order_number?.toString().slice(-4)}`}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Timer className={cn(isFullscreen ? "w-4 h-4" : "w-3 h-3")} />
                    <span className={cn("font-bold", isFullscreen ? "text-base" : "text-xs")}>
                        {order.wait_time_minutes || 0} min
                    </span>
                </div>
            </div>

            {/* Items */}
            <div className="px-3 py-2 bg-white">
                <div className="space-y-1.5">
                    {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                            <span className={cn(
                                "inline-flex items-center justify-center min-w-[24px] h-6 rounded bg-amber-100 text-amber-700 font-bold",
                                isFullscreen ? "text-sm" : "text-xs"
                            )}>
                                {item.quantity}x
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className={cn(
                                    "font-medium text-gray-800",
                                    isFullscreen ? "text-base" : "text-sm"
                                )}>
                                    {item.name}
                                </div>
                                {/* Formula parent mention */}
                                {item.is_formula_item && item.formula_name && (
                                    <div className="text-xs text-purple-600 font-medium flex items-center gap-1">
                                        <Coffee className="w-3 h-3" />
                                        {item.formula_name}
                                    </div>
                                )}
                                {item.supplements?.length > 0 && (
                                    <div className="text-xs text-gray-500 truncate">
                                        + {item.supplements.map(s => s.name || s).join(', ')}
                                    </div>
                                )}
                                {item.notes && (
                                    <div className="text-xs text-orange-600 italic flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">{item.notes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Server info */}
                {order.server_name && (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                        Serveur: {order.server_name}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
                <div className="flex gap-2">
                    {status === 'nouveau' && (
                        <Button
                            onClick={() => onCommencer(order.order_id)}
                            className={cn(
                                "flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold",
                                isFullscreen ? "h-12 text-base" : "h-9 text-sm"
                            )}
                        >
                            <Flame className={cn("mr-1", isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                            Préparer
                        </Button>
                    )}
                    {status === 'en_preparation' && (
                        <>
                            <Button
                                onClick={() => onPret(order.order_id)}
                                className={cn(
                                    "flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold",
                                    isFullscreen ? "h-12 text-base" : "h-9 text-sm"
                                )}
                            >
                                <CheckCircle className={cn("mr-1", isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                                Prêt
                            </Button>
                            <Button
                                onClick={() => onProblem(order)}
                                variant="outline"
                                className={cn(
                                    "bg-red-50 border-red-300 text-red-600 hover:bg-red-100",
                                    isFullscreen ? "h-12 px-4" : "h-9 px-3"
                                )}
                            >
                                <AlertTriangle className={cn(isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                            </Button>
                        </>
                    )}
                    {status === 'pret' && (
                        <Button
                            onClick={() => onServi(order.order_id)}
                            className={cn(
                                "flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold",
                                isFullscreen ? "h-12 text-base" : "h-9 text-sm"
                            )}
                        >
                            <Check className={cn("mr-1", isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                            Servi
                        </Button>
                    )}
                    {status === 'probleme' && (
                        <div className="flex-1 text-center py-2 text-red-600 font-bold flex items-center justify-center gap-2">
                            <AlertTriangle className={cn(isFullscreen ? "w-5 h-5" : "w-4 h-4")} />
                            Problème signalé
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ========================================
// PAGE PRINCIPALE - STATION BOISSONS CHAUDES
// ========================================

const HotBeveragesDashboardPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ nouveau: 0, en_preparation: 0, pret: 0, probleme: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('nouveau');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [problemDialog, setProblemDialog] = useState({ open: false, order: null });
    const [problemNote, setProblemNote] = useState('');
    const audioRef = useRef(null);
    const prevTicketCount = useRef(0);

    // Fetch tickets
    const fetchTickets = useCallback(async () => {
        try {
            const res = await api().get('/hot-beverages/tickets?include_all=true');
            const newTickets = res.data || [];
            
            // Play sound for new tickets
            if (soundEnabled && newTickets.length > prevTicketCount.current && prevTicketCount.current > 0) {
                playNotificationSound();
            }
            prevTicketCount.current = newTickets.length;
            
            setTickets(newTickets);
        } catch (error) {
            console.error('Error fetching hot beverage tickets:', error);
        } finally {
            setLoading(false);
        }
    }, [soundEnabled]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const res = await api().get('/hot-beverages/stats');
            setStats(res.data || { nouveau: 0, en_preparation: 0, pret: 0, probleme: 0, total: 0 });
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, []);

    // Play notification sound
    const playNotificationSound = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
        }
    };

    // Update ticket status
    const updateTicketStatus = async (orderId, status) => {
        try {
            await api().put(`/hot-beverages/tickets/${orderId}?status=${status}`);
            toast.success(
                status === 'en_preparation' ? 'Préparation commencée' :
                status === 'pret' ? 'Boisson prête !' :
                status === 'servi' ? 'Servi avec succès' : 'Statut mis à jour'
            );
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error updating ticket:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Report problem
    const reportProblem = async () => {
        if (!problemDialog.order) return;
        try {
            await api().post(`/hot-beverages/tickets/${problemDialog.order.order_id}/problem`, {
                problem_note: problemNote
            });
            toast.warning('Problème signalé');
            setProblemDialog({ open: false, order: null });
            setProblemNote('');
            fetchTickets();
            fetchStats();
        } catch (error) {
            console.error('Error reporting problem:', error);
            toast.error('Erreur lors du signalement');
        }
    };

    // Toggle fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    // Listen for fullscreen change
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Auto-refresh every 5 seconds
    useEffect(() => {
        fetchTickets();
        fetchStats();
        const interval = setInterval(() => {
            fetchTickets();
            fetchStats();
        }, 5000);
        return () => clearInterval(interval);
    }, [fetchTickets, fetchStats]);

    // Filter tickets by tab
    const filteredTickets = tickets.filter(t => {
        if (activeTab === 'nouveau') return t.boissons_chaudes_status === 'nouveau';
        if (activeTab === 'en_preparation') return t.boissons_chaudes_status === 'en_preparation';
        if (activeTab === 'pret') return t.boissons_chaudes_status === 'pret';
        if (activeTab === 'probleme') return t.boissons_chaudes_status === 'probleme';
        return true;
    });

    return (
        <div className={cn(
            "min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-zinc-900 dark:to-zinc-800",
            isFullscreen && "p-4"
        )}>
            {/* Audio for notifications */}
            <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />

            {/* Mobile Header */}
            {!isFullscreen && (
                <MobilePageHeader 
                    title="Station Boissons Chaudes" 
                    showBackButton={true}
                    backPath="/dashboard"
                />
            )}

            <div className={cn("p-4", !isFullscreen && "pt-2")}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Coffee className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className={cn(
                                "font-bold text-gray-900 dark:text-white",
                                isFullscreen ? "text-2xl" : "text-xl"
                            )}>
                                Boissons Chaudes
                            </h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {stats.total} commande{stats.total > 1 ? 's' : ''} active{stats.total > 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setSoundEnabled(!soundEnabled)}
                            className={cn(
                                "rounded-full",
                                soundEnabled ? "text-emerald-600" : "text-gray-400"
                            )}
                        >
                            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={toggleFullscreen}
                            className="rounded-full"
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => { fetchTickets(); fetchStats(); }}
                            className="rounded-full"
                        >
                            <RefreshCcw className="w-5 h-5" />
                        </Button>
                        {!isFullscreen && (
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => navigate('/dashboard')}
                                className="rounded-full"
                            >
                                <Home className="w-5 h-5" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 shadow-sm border border-amber-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                                <Bell className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-600">{stats.nouveau}</p>
                                <p className="text-xs text-gray-500">Nouvelles</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 shadow-sm border border-orange-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Flame className="w-4 h-4 text-orange-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-orange-600">{stats.en_preparation}</p>
                                <p className="text-xs text-gray-500">En cours</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 shadow-sm border border-emerald-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-emerald-600">{stats.pret}</p>
                                <p className="text-xs text-gray-500">Prêtes</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 shadow-sm border border-red-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-600">{stats.probleme}</p>
                                <p className="text-xs text-gray-500">Problèmes</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid grid-cols-4 mb-4 h-12">
                        <TabsTrigger value="nouveau" className="text-sm data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                            <Bell className="w-4 h-4 mr-1" />
                            Nouvelles ({stats.nouveau})
                        </TabsTrigger>
                        <TabsTrigger value="en_preparation" className="text-sm data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                            <Flame className="w-4 h-4 mr-1" />
                            En cours ({stats.en_preparation})
                        </TabsTrigger>
                        <TabsTrigger value="pret" className="text-sm data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Prêtes ({stats.pret})
                        </TabsTrigger>
                        <TabsTrigger value="probleme" className="text-sm data-[state=active]:bg-red-500 data-[state=active]:text-white">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Problèmes ({stats.probleme})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value={activeTab} className="mt-0">
                        {loading ? (
                            <div className="flex items-center justify-center h-40">
                                <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="text-center py-16">
                                <Coffee className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                <p className="text-gray-500">
                                    {activeTab === 'nouveau' && "Aucune nouvelle commande"}
                                    {activeTab === 'en_preparation' && "Aucune commande en préparation"}
                                    {activeTab === 'pret' && "Aucune commande prête"}
                                    {activeTab === 'probleme' && "Aucun problème signalé"}
                                </p>
                            </div>
                        ) : (
                            <div className={cn(
                                "grid gap-4",
                                isFullscreen ? "grid-cols-4" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                            )}>
                                {filteredTickets.map(ticket => (
                                    <HotBeverageTicket
                                        key={ticket.order_id}
                                        order={ticket}
                                        isFullscreen={isFullscreen}
                                        onCommencer={(id) => updateTicketStatus(id, 'en_preparation')}
                                        onPret={(id) => updateTicketStatus(id, 'pret')}
                                        onServi={(id) => updateTicketStatus(id, 'servi')}
                                        onProblem={(order) => setProblemDialog({ open: true, order })}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Problem Dialog */}
            <Dialog open={problemDialog.open} onOpenChange={(open) => !open && setProblemDialog({ open: false, order: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Signaler un Problème
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Commande: {problemDialog.order?.table_number ? `Table ${problemDialog.order.table_number}` : `#${problemDialog.order?.order_number}`}
                        </p>
                        <Textarea
                            value={problemNote}
                            onChange={(e) => setProblemNote(e.target.value)}
                            placeholder="Décrivez le problème (produit manquant, erreur, etc.)"
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setProblemDialog({ open: false, order: null })}>
                            Annuler
                        </Button>
                        <Button onClick={reportProblem} className="bg-red-500 hover:bg-red-600 text-white">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Signaler
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HotBeveragesDashboardPage;
